import React, { useLayoutEffect, useRef } from 'react'
import Select, { components } from 'react-select'
import cx from 'classnames'

import { CountryListItem } from 'components/common/types'
import { getCountryListMap, getCountryFlag } from 'country-flags-dial-code'
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters'
import flagIconGenerator from 'components/common/flagIconGenerator'

const countryDictionary: CountryListItem[] = getCountryListMap()
const addedManualCountries = [
  {
    code: 'Caribe-Islands',
    country: 'Caribbean Netherlands - Bonaire/St. Eustatius',
    dialCode: '+599',
    flag: getCountryFlag('NL')
  },
  {
    code: 'CURACAO',
    country: 'Curaçao',
    dialCode: '+599',
    custom: true
  },
  {
    code: 'SABA',
    country: 'Saba',
    dialCode: '+599',
    custom: true
  }
]

const countryList = [
  ...Object.values(countryDictionary),
  ...addedManualCountries
].sort((a, b) => a.country.localeCompare(b.country))

const MenuList = (props) => {
  return (
    <components.MenuList className="w-full md:w-[350px] bg-white" {...props}>
      {props.children}
    </components.MenuList>
  )
}

type Props = {
  phoneDialDetails: CountryListItem | null;
  handleCountryCodeChange: (countryListItem: CountryListItem) => void;
  phone: string;
  phoneOnChangeHandler: (value: string) => void;
  phoneNumberLabel?: string; // Optional prop for custom label
  isPhoneEnabled?: boolean; // New optional prop to control the disabled state
};


const PhoneCountryCodeRow = ({
  phoneDialDetails,
  handleCountryCodeChange,
  phone,
  phoneOnChangeHandler,
  phoneNumberLabel = 'Phone Number', // Default value if not provided
  isPhoneEnabled = true // Default
}: Props) => {
  const phoneCodeRef = useRef<HTMLInputElement | null>(null)

  const onPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const re = /^[0-9\b]+$/

    if (event.target.value === '' || re.test(event.target.value)) {
      phoneOnChangeHandler(event.target.value)
    }
  }

  const setDialCodeInputWidth = (dialCode: string) => {
    phoneCodeRef.current!.style.display = 'block'
    phoneCodeRef.current!.style.maxWidth = `${dialCode.length * 12}px`
  }

  useLayoutEffect(() => {
    if (phoneDialDetails?.dialCode) {
      setDialCodeInputWidth(phoneDialDetails?.dialCode)
    }
  }, [])

  const countryChangeHandler = (option: CountryListItem) => {
    handleCountryCodeChange(option)
    phoneOnChangeHandler('')

    setDialCodeInputWidth(option.dialCode)
  }

  const filterCountrySearchedList = (
    option: FilterOptionOption<CountryListItem>,
    inputValue: string
  ) =>
    `${option.data.country} ${option.data.dialCode}`
      .toLowerCase()
      .includes(inputValue.toLowerCase().trim())

  return (
    <div>
      <h4 className="mb-4">{phoneNumberLabel}</h4>

      <div className="flex flex-col md:flex-row md:space-x-4 space-y-6 md:space-y-0">
        <Select
          value={phoneDialDetails}
          className="w-full md:w-[45%]"
          options={countryList}
          placeholder="Select Country"
          menuPlacement="top"
          components={{
            MenuList
          }}
          styles={{
            control: (props, { isFocused }) => ({
              ...props,
              // required color
              backgroundColor: '#ebebeb',
              border: '1px solid black !important',
              boxShadow: isFocused ? 'black' : 'black'
            })
          }}
          filterOption={filterCountrySearchedList}
          getOptionLabel={(option: CountryListItem) => (
            <div className="flex items-center mr-4">
              {option?.custom ? (
                flagIconGenerator(option.country)
              ) : (
                <div
                  className="w-12 h-12 flex"
                  dangerouslySetInnerHTML={{ __html: option.flag }}
                />
              )}

              <div className="w-full text-center ml-4">
                {option.country} ({option.dialCode})
              </div>
            </div>
          )}
          getOptionValue={(option: CountryListItem) => option.code}
          onChange={countryChangeHandler}
        />

        <div className="relative flex w-full flex-1 text-[1.6rem]">
          <input
            ref={phoneCodeRef}
            value={phoneDialDetails?.dialCode}
            disabled
            className="pl-2 bg-required max-w-[6rem] items-center border-y border-l border-black border-solid rounded-tl-md rounded-bl-md text-[1.6rem] text-black hidden"
          />

          <input
            placeholder={phoneDialDetails ? '' : 'Phone * (7-18 Digits)'}
            className={cx(
              'flex-1 h-full bg-required flex items-center p-2 pl-1 border border-black border-solid rounded-md text-[1.6rem] outline-none',
              {
                'rounded-bl-none rounded-tl-none border-l-0': !!phoneDialDetails
              }
            )}
            minLength={7}
            maxLength={11}
            value={phone}
            autoComplete="off"
            onChange={onPhoneChange}
            disabled={!isPhoneEnabled || !phoneDialDetails}
          />
        </div>
      </div>
    </div>
  )
}

export default PhoneCountryCodeRow
